import BetSlipApiService from "@api-services/agency/BetSlipApiService";
import { BetSlipExtended } from "@api-types/agency/bet-slip";
import { Filter, rawFilter } from "@api-types/my-bets/Filter";
import { UserAccountType } from "@common/constants";
import { FormattedLookup } from "@data-types/my-bets/Lookups";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { Bet } from "@models";
import { localizationService } from "@state";
import { history } from "@state";
import { LoaderStore } from "@state/stores/common";
import { CashoutStore } from "@state/stores/my-bets/CashoutStore";
import { MyBetsFilterStore } from "@state/stores/my-bets/MyBetsFilterStore";
import type { RootStore } from "@state/stores/types";
import {
	BetPeriod,
	BetStatus,
	betPeriods,
	buildBettingAccountTypeString,
	getCurrentCulture,
	getWalletByShopIdAndAccountTypeId,
} from "@utils";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import {
	getEventIdsFromMyBets,
	mapBetSlipOffers,
} from "@utils/my-bets/helpers";
import {
	IReactionDisposer,
	action,
	computed,
	observable,
	runInAction,
} from "mobx";

import { ActiveEventsStore } from "./ActiveEventsStore";
import { BetCancelStore } from "./BetCancelStore";
import { BetDetailsModalStore } from "./BetDetailsModalStore";
import { ReportsStore } from "./ReportsStore";
import { ReuseBetSlipStore } from "./ReuseBetSlipStore";
import ShareBetSlipStore from "./ShareBetSlipStore";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const LookupService = lazy(loadFailPath, ()=>import("@services/LookupService"));

export class BetViewType extends Bet {
	needsAdditionalDetails?: boolean;

	constructor(bet: BetSlipExtended, needsDetails: boolean) {
		super(bet);
		this.needsAdditionalDetails = needsDetails;
	}
}

export default class BaseMyBetsStore {
	type: "MyBetsStore" = "MyBetsStore";

	location: "MyBetsPage" | "AdditionalOffer" | "BetDetails";
	private filterReaction: IReactionDisposer | null;

	rootStore: RootStore;
	cashoutStore: CashoutStore;
	reuseBetSlipStore: ReuseBetSlipStore;
	betsReportStore: ReportsStore;
	activeEventsStore: ActiveEventsStore;
	betCancelStore: BetCancelStore;
	betSlipApiService: BetSlipApiService;
	shareBetSlipStore: ShareBetSlipStore;
	loader: LoaderStore;
	myBetsFilterStore: MyBetsFilterStore;
	betDetailsModalStore: BetDetailsModalStore;
	pageSize = 10;

	constructor(
		rootStore: RootStore,
		location: "MyBetsPage" | "AdditionalOffer" | "BetDetails"
	) {
		this.rootStore = rootStore;
		this.location = location;
		// Helper stores
		this.cashoutStore = new CashoutStore(this);
		this.reuseBetSlipStore = new ReuseBetSlipStore(rootStore);
		this.betsReportStore = new ReportsStore(rootStore, this);
		this.activeEventsStore = new ActiveEventsStore(rootStore);
		this.betCancelStore = new BetCancelStore(rootStore, this);
		this.shareBetSlipStore = new ShareBetSlipStore(rootStore);
		this.myBetsFilterStore = new MyBetsFilterStore(rootStore, this);
		this.betDetailsModalStore = new BetDetailsModalStore(rootStore, this);

		this.loader = new LoaderStore();

		// Method bindings
		this.getDateRange = this.getDateRange.bind(this);
	}

	//#region observables

	@observable isStoreInitialized = false;
	@observable isFetchingData = true; // When store first starts it will always start fetching

	/** Array of eventIds */
	@observable rememberMyBetsFilter = null;

	@observable betsList: BetViewType[] = [];

	@observable betCombinationsMap = observable.map<string, {} | null>();

	@observable betSlipStatusLookups: FormattedLookup[] = [];
	@observable betStatusLookups: FormattedLookup[] = [];

	@computed get betPeriods() {
		return betPeriods;
	}

	@observable pageNumber = 1;

	@observable totalItems = 0;

	//#endregion observables

	//#region computed

	@computed get shouldShowSkeletonLoader() {
		return this.isLoadingProcess;
	}

	@computed get shouldShowEmptyState() {
		return (
			this.betsList.length === 0 &&
			!this.isFetchingData &&
			this.isStoreInitialized
		);
	}

	@computed get shouldShowPager() {
		return !this.isLoadingProcess && !this.shouldShowEmptyState;
	}

	@computed get isLoading() {
		return this.loader.isLoading;
	}

	@computed get isLoadingProcess(): boolean {
		return this.loader.isLoadingProcess;
	}

	@computed get walletId() {
		return this.myBetsFilterStore?.walletId;
	}

	/**
	 * @returns {string} account type id
	 */
	@computed get onlineAccountType() {
		return this.rootStore.userAuthStore.availableWallets?.find(
			(type) => type.abrv === UserAccountType.SportBettingAccountOnline
		);
	}

	/**
	 * @returns {string} account type id
	 */
	@computed get onlineShopAccountType() {
		return this.rootStore.userAuthStore.availableWallets?.find(
			(type) =>
				type.abrv === UserAccountType.SportBettingAccountShopOnline
		);
	}

	@computed get closedBetsBetStatuses() {
		return this.betStatusLookups?.filter(
			(status) => status.abrv !== BetStatus.OPEN
		);
	}

	@computed get openBetsBetStatus() {
		return this.betStatusLookups?.filter(
			(status) => status.abrv === BetStatus.OPEN
		);
	}

	//#endregion computed

	//#region actions

	@action.bound
	async onInitialize() {
		this.loader.suspend();
		this.cashoutStore.onInitialize();
		this.myBetsFilterStore.onInitialize();

		this.disposeFilterReaction();

		await this.submitFilter();

		this.isStoreInitialized = true;
	}

	@action.bound
	async fetchBetLookups() {
		this.loader.suspend();
		this.isFetchingData = true;

		let betSlipStatusLookupsPrimary: FormattedLookup[] = [];
		let betStatusesLookupsPrimary: FormattedLookup[] = [];

		try {
			betSlipStatusLookupsPrimary = await (
				await LookupService
			).default.findBetSlipStatuses({
				context: RequestContextType.Wallet,
				walletId: this.walletId,
			});

			betStatusesLookupsPrimary = await (
				await LookupService
			).default.findBetStatuses({
				context: RequestContextType.Wallet,
				walletId: this.walletId,
			});
		} catch (error) {
			console.error(error);
		}

		// Initialize filter after this store, we need to know if search params are empty

		runInAction(() => {
			this.betSlipStatusLookups = betSlipStatusLookupsPrimary;
			this.betStatusLookups = betStatusesLookupsPrimary;
			this.isStoreInitialized = true;
			this.loader.resume();
			this.isFetchingData = false;
		});
	}

	@action.bound
	async fetchBets(filter: Filter): Promise<void> {
		this.betsList = [];
		this.loader.suspend();
		this.isFetchingData = true;
		this.cashoutStore.disposeSubscription();
		try {
			const response = await BetSlipApiService.findBets(
				filter,
				filter.pageNumber,
				this.pageSize,
				createApiRequestOptions(
					RequestContextType.Wallet,
					this.walletId
				)
			);
			response.item.forEach((betslip) => mapBetSlipOffers(betslip));

			const bets: BetViewType[] = response.item.map(
				(bet) => new BetViewType(bet, false)
			);

			const eventIdsFromBets = getEventIdsFromMyBets(bets);

			this.activeEventsStore.fetchActiveEvents(
				eventIdsFromBets,
				this.myBetsFilterStore.rawData.betStatusAbrv
			);

			runInAction(() => {
				this.betsList = bets;
				this.betDetailsModalStore?.closeModal?.();
				this.betDetailsModalStore.onNewPage();
				this.pageNumber = response.page;
				this.totalItems = response.totalRecords;
				this.cashoutStore.initializeCashoutSubscription();
				this.loader.resume();
				this.isFetchingData = false;
			});
		} catch (error) {
			runInAction(() => {
				console.error(error);
				const errorMessage = localizationService.t("ERROR.TITLE");
				this.rootStore.notificationStore.error(errorMessage);
				this.betDetailsModalStore?.closeModal?.();
				this.betsList = [];
				this.loader.resume();
				this.isFetchingData = false;
			});
		}
	}

	@action.bound
	refresh() {
		if (this.location == "MyBetsPage") {
			this.fetchBets(this.myBetsFilterStore.filter);
		}
	}

	@action.bound
	async initBetCombinationForBetSlip(bet: Bet) {
		if (this.betCombinationsMap.has(bet.id)) {
			return;
		}

		this.betCombinationsMap.set(bet.id, null);
		let userAccount = getWalletByShopIdAndAccountTypeId(
			bet.shopId,
			bet.bettingAccountTypeId,
			this.rootStore.userAuthStore.userAccounts || []
		);
		let context = this.rootStore.userAuthStore.mapUserIdToUserContext(
			bet.userId
		);
		if (userAccount) {
			context = createApiRequestOptions(
				RequestContextType.Wallet,
				userAccount.id
			);
		}
		const combinations = await BetSlipApiService.getSystemCombinations(
			bet.id,
			context
		);
		this.betCombinationsMap.set(bet.id, combinations);
	}

	@action.bound
	setAccountTypeAndFetch(accountTypeAbrv: UserAccountType) {
		this.myBetsFilterStore.setAccountTypeIds(accountTypeAbrv);
		this.submitFilter();
	}

	@action.bound
	async submitFilter() {
		this.isFetchingData = true;
		if (
			!this.myBetsFilterStore.rawData.fetchReport ||
			!(
				this.myBetsFilterStore.filter.from &&
				this.myBetsFilterStore.filter.to &&
				this.myBetsFilterStore.rawData.bettingAccountTypeAbrv
			)
		) {
			this.betsReportStore.onDispose();
		} else {
			this.betsReportStore.fetchReports(
				{
					bettingAccountTypeAbrv: buildBettingAccountTypeString({
						bettingAccountTypeAbrv:
							this.myBetsFilterStore.rawData
								.bettingAccountTypeAbrv,
						rootStore: this.rootStore,
						shopId: this.myBetsFilterStore.rawData.shopId,
					}),
					from: this.myBetsFilterStore.filter.from,
					to: this.myBetsFilterStore.filter.to,
				},
				this.walletId
			);
		}

		await this.fetchBetLookups();
		await this.fetchBets(this.myBetsFilterStore.filter);

		this.cashoutStore.closeCashoutModal();

		this.isFetchingData = false;
	}

	@action.bound
	onPageChange(newPageNumber: number) {
		window.scrollTo(0, 0);
		history.replace({
			pathname: window.location.pathname,
			state: {
				...history.location?.state,
				scrollPositionApp: 0,
			},
		});
		this.myBetsFilterStore.setRawFilterData({
			...this.myBetsFilterStore.rawData,
			pageNumber: newPageNumber,
		});
		this.submitFilter();
	}

	//#endregion actions

	//#region helpers

	getDateRange(data: rawFilter) {
		const { period, from, to } = data;

		if (
			(period == null && from == null && to == null) ||
			period === "all"
		) {
			return null;
		}

		if (period !== BetPeriod.custom) {
			const timeInterval = this.betPeriods
				.find((p) => p.value === period)
				?.getTimeInterval();
			if (timeInterval == null) {
				// Return default
				return this.betPeriods[0].getTimeInterval();
			}
			return timeInterval;
		}

		return { from, to };
	}

	onTabChange(tab: BetStatus) {
		this.myBetsFilterStore.setActiveTab(tab);

		this.myBetsFilterStore.setRawFilterData(
			this.myBetsFilterStore.defaultTabsFilter(tab)
		);

		this.submitFilter();
	}

	//#endregion helpers

	//#region disposers

	@action.bound
	onDispose() {
		this.cashoutStore.onDispose();
		this.reuseBetSlipStore.onDispose();
		this.betCancelStore.onDispose();
		this.myBetsFilterStore.onDispose();
		this.disposeFilterReaction();
		this.activeEventsStore.onDispose();
		this.isStoreInitialized = false;
		this.isFetchingData = true;
		this.shareBetSlipStore.onDispose();
		this.betSlipStatusLookups = [];
		this.betStatusLookups = [];

		this.disposeCurrentMyBets();
	}

	@action.bound
	disposeCurrentMyBets() {
		this.rememberMyBetsFilter = null;

		this.betsList = [];

		this.betCombinationsMap.clear();
	}

	@action.bound
	disposeFilterReaction() {
		if (this.filterReaction != null) {
			this.filterReaction();
			this.filterReaction = null;
		}
	}

	//#endregion disposers
}
